import { useCallback, useEffect, useState } from 'react'

import { useFetch, useRemoteCollection } from 'civic-champs-shared/api/hooks'
import { useCurrentOrg } from 'auth/hooks'
import useCancelEventPrompt from 'Event/modules/events/details/header/useCancelEventPrompt'
import { getSlots } from '../helpers/volunteerSlots'
import { useGetRawEvents } from './useGetRawEvents'
import { mapRawEventsResponse } from '../helpers/mapRawEventsResponse'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'

const useEventsCollection = ({ startDate = null, endDate = null, debounceDelay = 0 }) => {
  const [request, { loading, error }] = useFetch()
  const [syntheticLoading, setSyntheticLoading] = useState(false)
  const [events, operations, listeners] = useRemoteCollection()
  const organization = useCurrentOrg()
  const { syncCollection, eagerRemove } = operations

  const isEnabled = useFeatureEnabled('ClientSideEventProcessing')
  const [getRawEvents, { error: rawEventsError }] = useGetRawEvents()

  const getEvents = useCallback(async () => {
    try {
      let events

      if (isEnabled) {
        const response = await getRawEvents({
          from: startDate,
          to: endDate,
        })

        events = mapRawEventsResponse(response)
      } else {
        let queryStringParameters = { status: 'Publish' }
        if (startDate && endDate) {
          queryStringParameters = { startDate, endDate, status: 'any' }
        }

        events = (
          await request({
            method: 'get',
            url: `/organizations/${organization.id}/opportunity_instances`,
            config: { queryStringParameters },
          })
        ).list
      }

      const updatedEvents = events.map(event => {
        event.slots = getSlots(event)
        return event
      })

      syncCollection(updatedEvents)
    } finally {
      // a failed fetch used to leave the page spinning forever
      setSyntheticLoading(false)
    }
  }, [isEnabled, syncCollection, getRawEvents, startDate, endDate, request, organization.id])

  useEffect(() => {
    setSyntheticLoading(true)
    let timeout = setTimeout(getEvents, debounceDelay)
    return () => clearTimeout(timeout)
  }, [debounceDelay, getEvents])

  const onEventCancelled = useCallback(
    ({ event, cancelType }) => {
      if (!cancelType || cancelType === 'single') {
        eagerRemove(event)
      } else {
        getEvents()
      }
    },
    [eagerRemove, getEvents],
  )

  const cancelEvent = useCancelEventPrompt(onEventCancelled)

  return [
    {
      events,
      loading: loading || syntheticLoading,
      error: error || rawEventsError,
    },
    {
      refresh: getEvents,
      cancelEvent,
      eagerRemoveMany: operations.eagerRemoveMany,
    },
    listeners,
  ]
}

export default useEventsCollection
